import { getChannel } from '~/modules/analytics/channel';
import { APPX_ORG_API_URL } from '~/modules/config';
import { generateBaseUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { ListingResponse } from '~/modules/services/listing_service';
import { leadCollectionStore, LeadFormData } from '~/modules/stores/leadCollectionStore';
import { listingStore, selectListing } from '~/modules/stores/listingStore';
import { routerStore, selectMatch } from '~/modules/stores/routerStore';
import { timeoutPromise } from '~/modules/utilities/browser';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { HttpRequestConfig } from '~/modules/utilities/http';
import { setupOrgRequestConfig } from '~/modules/utilities/httpUtilities';
import {
    EXPIRATION_DATE,
    getUtmParamsFromLocalStorage,
    isExpired,
    REFERRAL_CODE_PARAM,
} from '~/modules/utilities/localStorageUtilities';

export const USER_ACTIVITY_POST_TIMEOUT_MS = 500;
export const USER_SOURCE = 'OneAppExchange';

export interface LeadPayload extends LeadFormData {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    referral_code?: string;
    other_source?: string;
    listing_URL?: string;
}

export interface UserActivityPayload {
    listingId: string;
    type: string;
    source: string;
    userAgent?: string;
    lead?: LeadPayload;
}

export function postKeepAlive<T extends object>(url: string, data: T): Promise<void> {
    const requestConfig: HttpRequestConfig = setupOrgRequestConfig();
    const requestPromise = fetch(url, {
        method: 'POST',
        keepalive: true,
        cache: 'no-cache',
        headers: {
            ...requestConfig.headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
    }).then(async (response) => {
        if (response.status !== 201) {
            throw new Error(`Incorrect response: status=${response.status}, data=${await response.text()}`);
        }
    });
    // If the request takes more than USER_ACTIVITY_POST_TIMEOUT_MS, resolve the promise and allow users to proceed
    return Promise.race([requestPromise, timeoutPromise(USER_ACTIVITY_POST_TIMEOUT_MS)]);
}

export function getUserActivityUrl(listingId: string): string {
    return `${APPX_ORG_API_URL}/listings/${listingId}/user-activities`;
}

export function getUserInstallUrl(listingId: string): string {
    return `${APPX_ORG_API_URL}/listings/${listingId}/user-installs`;
}

export function generateLeadData(listingId: string): LeadPayload {
    const payload = getPayloadFromLocalStorage(listingId);
    const listingUrl = `${generateBaseUrl()}/${Routes.LISTING}?listingId=${listingId}`;

    if (payload) {
        const formData = leadCollectionStore.getState().formData;
        const utmData = payload.UTM;
        const sourceData = payload.source;

        const utmExpired = utmData && isExpired(utmData[EXPIRATION_DATE]);
        const sourceExpired = sourceData && isExpired(sourceData[EXPIRATION_DATE]);

        const utmParams = utmData && !utmExpired ? getUtmParamsFromLocalStorage(listingId) : null;
        const referralCode = sourceData && !sourceExpired ? sourceData[REFERRAL_CODE_PARAM] : '';

        // Merge the lead data with UTM params and referral code if available
        return {
            ...formData,
            ...utmParams,
            referral_code: referralCode,
            listing_URL: listingUrl,
        };
    }

    return generateFallbackLeadData(listingUrl);
}

function getPayloadFromLocalStorage(listingId: string) {
    const payloadString = localStorage.getItem(listingId);
    return payloadString ? JSON.parse(payloadString) : null;
}

function generateFallbackLeadData(listingUrl: string): LeadPayload {
    const currentUrl = sanitizeText(window.location.href);
    const documentReferrer = sanitizeText(document.referrer);
    const otherSourceInfo = getChannel(currentUrl, documentReferrer, false, false, false);

    return {
        ...leadCollectionStore.getState().formData,
        listing_URL: listingUrl,
        other_source: otherSourceInfo.channel,
    };
}

export function getQueryStringFromRouter(): string {
    return selectMatch(routerStore.getState()).queryString;
}

export function getListingIdFromQueryString(queryString: string): string | null {
    const queryParams = new URLSearchParams(queryString);
    return queryParams.get('listingId');
}

export function getListingFromStore(listingId: string): ListingResponse | null {
    return selectListing(listingStore.getState(), listingId) || null;
}
